import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, CheckCircle2, ChevronRight, Settings2, RefreshCw, X,
  Siren, BellRing, Rocket, Boxes, BarChart3, Archive, GitBranch, Inbox,
} from 'lucide-react';
import { dashboard, type DashboardBereich } from '../api';

/**
 * Was gerade zählt — und nur das.
 *
 * ══ SORTIERT NACH DRINGLICHKEIT, NICHT NACH THEMA ══════════════════════════
 *
 * Jeder Bereich meldet vom Server mit, wie dringend er ist. Danach wird
 * sortiert. Ein Dashboard mit fester Reihenfolge zeigt im Ernstfall die
 * Ausrollhistorie oben und den Ausfall unten — und wer es täglich sieht, hört
 * auf hinzusehen.
 *
 * ══ WAS RUHIG IST, WIRD KLEIN ══════════════════════════════════════════════
 *
 * Bereiche ohne Auffälligkeit schrumpfen auf eine Zeile. Sie verschwinden
 * nicht: „keine offenen Alarme" ist eine Auskunft, und zwar eine, die man
 * sehen will. Aber sie braucht keine halbe Bildschirmhöhe.
 *
 * ══ WER WAS SIEHT, ENTSCHEIDET DER NUTZER ══════════════════════════════════
 *
 * Die Auswahl liegt im Browser, nicht am Konto: sie ist eine Vorliebe und
 * keine Berechtigung. Wer sie auf einem zweiten Rechner anders will, soll das
 * dürfen, ohne dass es jemanden kümmert.
 */

const SPEICHER = 'pc.dashboard.ausgeblendet';

const SYMBOLE: Record<string, typeof Siren> = {
  alarme: Siren,
  bereitschaft: BellRing,
  deployments: Rocket,
  instanzen: Boxes,
  reichweite: BarChart3,
  sicherungen: Archive,
  git: GitBranch,
  feedback: Inbox,
};

const RAHMEN: Record<number, string> = {
  0: 'border-slate-200',
  1: 'border-amber-300',
  2: 'border-rose-300',
};

const ZAHLFARBE: Record<number, string> = {
  0: 'text-slate-800',
  1: 'text-amber-700',
  2: 'text-rose-700',
};

function ausgeblendetLesen(): Set<string> {
  try {
    const roh = localStorage.getItem(SPEICHER);
    return new Set(roh ? (JSON.parse(roh) as string[]) : []);
  } catch {
    // Privates Fenster, gesperrte Website-Daten: dann eben alles zeigen.
    return new Set();
  }
}

function Kachel({ b }: { b: DashboardBereich }) {
  const Symbol = SYMBOLE[b.schluessel] ?? Boxes;
  const ruhig = b.dringlichkeit === 0 && !b.fehler;

  return (
    <Link
      to={b.pfad}
      className={`block rounded-xl border bg-surface p-4 transition hover:shadow-sm ${
        RAHMEN[b.dringlichkeit] ?? 'border-slate-200'
      } ${ruhig ? 'opacity-75 hover:opacity-100' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <Symbol size={13} className="text-slate-400" />
          {b.titel}
        </span>
        <ChevronRight size={13} className="shrink-0 text-slate-300" />
      </div>

      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span
          className={`text-2xl font-semibold tabular-nums ${
            ZAHLFARBE[b.dringlichkeit] ?? 'text-slate-800'
          }`}
        >
          {b.zahl ?? '—'}
        </span>
        <span className="text-[11px] text-slate-500">{b.einheit}</span>
      </div>

      {b.fehler ? (
        <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-700">
          <AlertTriangle size={11} className="mt-px shrink-0" />
          {b.hinweis}
        </p>
      ) : b.hinweis ? (
        <p
          className={`mt-1 text-[11px] ${
            b.dringlichkeit === 2
              ? 'text-rose-700'
              : b.dringlichkeit === 1
                ? 'text-amber-700'
                : 'text-slate-500'
          }`}
        >
          {b.hinweis}
        </p>
      ) : (
        <p className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700">
          <CheckCircle2 size={11} /> nichts Offenes
        </p>
      )}

      {/* Einzelheiten nur, wo es etwas zu sehen gibt. */}
      {b.dringlichkeit > 0 && b.eintraege.length > 0 && (
        <ul className="mt-2 space-y-0.5 border-t border-slate-100 pt-2">
          {b.eintraege.slice(0, 4).map((e) => (
            <li key={e.id} className="flex items-baseline justify-between gap-2 text-[11px]">
              <span className="truncate text-slate-700">{e.text}</span>
              {e.neben && <span className="shrink-0 text-slate-400">{e.neben}</span>}
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}

export default function DashboardPage() {
  const [ausgeblendet, setAusgeblendet] = useState<Set<string>>(ausgeblendetLesen);
  const [einstellungen, setEinstellungen] = useState(false);
  const [nurAuffaellige, setNurAuffaellige] = useState(false);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['dashboard', 'uebersicht'],
    queryFn: dashboard.uebersicht,
    // Eine Minute: häufig genug, dass ein Ausfall auffällt, selten genug, dass
    // acht Abfragen nicht zur Dauerlast werden.
    refetchInterval: 60_000,
  });

  useEffect(() => {
    try {
      localStorage.setItem(SPEICHER, JSON.stringify([...ausgeblendet]));
    } catch {
      /* siehe ausgeblendetLesen */
    }
  }, [ausgeblendet]);

  const alle = data?.bereiche ?? [];
  const sichtbar = alle
    .filter((b) => !ausgeblendet.has(b.schluessel))
    .filter((b) => !nurAuffaellige || b.dringlichkeit > 0 || b.fehler)
    // Dringend zuerst, sonst in der Reihenfolge vom Server.
    .sort((a, b) => b.dringlichkeit - a.dringlichkeit);

  const dringend = alle.filter((b) => b.dringlichkeit === 2).length;
  const hinsehen = alle.filter((b) => b.dringlichkeit === 1).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Überblick</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            {dringend > 0
              ? `${dringend} ${dringend === 1 ? 'Bereich braucht' : 'Bereiche brauchen'} jetzt Aufmerksamkeit.`
              : hinsehen > 0
                ? `Nichts Dringendes. ${hinsehen} ${hinsehen === 1 ? 'Bereich' : 'Bereiche'} zum Hinsehen.`
                : 'Alles ruhig.'}
            {data && (
              <span className="ml-1 text-slate-400">
                Stand {new Date(data.erzeugtAm).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <input
              type="checkbox"
              checked={nurAuffaellige}
              onChange={(e) => setNurAuffaellige(e.target.checked)}
            />
            nur Auffälliges
          </label>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Neu laden"
            className="rounded-lg border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setEinstellungen((v) => !v)}
            title="Welche Bereiche angezeigt werden"
            className={`rounded-lg border p-1.5 ${
              einstellungen
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Settings2 size={13} />
          </button>
        </div>
      </div>

      {einstellungen && (
        <div className="rounded-xl border border-slate-200 bg-surface p-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold text-slate-700">Angezeigte Bereiche</h2>
            <button
              onClick={() => setEinstellungen(false)}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-100"
            >
              <X size={13} />
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {alle.map((b) => {
              const an = !ausgeblendet.has(b.schluessel);
              return (
                <button
                  key={b.schluessel}
                  onClick={() =>
                    setAusgeblendet((alt) => {
                      const neu = new Set(alt);
                      if (neu.has(b.schluessel)) neu.delete(b.schluessel);
                      else neu.add(b.schluessel);
                      return neu;
                    })
                  }
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                    an
                      ? 'bg-brand-600 text-white'
                      : 'border border-slate-300 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {b.titel}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] text-slate-500">
            Die Auswahl gilt nur in diesem Browser — sie ist eine Vorliebe, keine Berechtigung.
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      ) : sichtbar.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-surface px-4 py-10 text-center">
          <CheckCircle2 size={20} className="mx-auto text-emerald-500" />
          <p className="mt-2 text-sm text-slate-600">
            {nurAuffaellige ? 'Nichts Auffälliges.' : 'Alle Bereiche ausgeblendet.'}
          </p>
          {nurAuffaellige && (
            <button
              onClick={() => setNurAuffaellige(false)}
              className="mt-1 text-xs text-brand-700 underline"
            >
              alle zeigen
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {sichtbar.map((b) => (
            <Kachel key={b.schluessel} b={b} />
          ))}
        </div>
      )}
    </div>
  );
}
